import { InputAdornment, TextField as MuiTextField } from '@mui/material';
import { cn } from 'cn';
import {
  type ComponentProps,
  forwardRef,
  type ReactNode,
  useId,
  useState,
} from 'react';

import {
  getFormattedNumber,
  isFieldRequired,
  parseFromInput,
  unique,
} from '@vestigas/utils';

import { ValidationError } from '@vestigas/ui/molecules';

import { useFieldContext } from './form-context';
import { InputLabel } from './InputLabel';

type P = {
  readonly flex?: '1' | 'auto' | 'initial' | 'none';
  readonly hint?: string;
  readonly icon?: ReactNode;
  readonly unit?: string;
  readonly isDisabled?: boolean;
  readonly showError?: boolean;
  /**
   * Maximum number of decimal places to display.
   * @default 10
   */
  readonly maxDigits?: number;
} & Omit<ComponentProps<typeof MuiTextField>, 'type'>;

/**
 * Formats a number for display in an input field.
 * Uses getFormattedNumber with useGrouping: false (no thousand separators).
 */
function formatForInput(
  value: number | null | undefined,
  maxDigits: number | 'auto',
): string {
  if (value === null || value === undefined) {
    return '';
  }

  return getFormattedNumber({
    digits: maxDigits ?? 'auto',
    fallbackValue: '',
    useGrouping: false,
    value,
  });
}

/**
 * A localized number input field that:
 * - Displays numbers in the user's locale format (e.g., "1234,5" for German)
 * - Accepts localized input (comma or dot as decimal separator)
 * - Stores the value as a number in form state for reliable comparison
 */
export const LocalizedNumberField = forwardRef<HTMLInputElement, P>(
  (
    {
      'aria-label': ariaLabel,
      autoComplete = 'off',
      className,
      flex = '1',
      hint,
      isDisabled = false,
      label,
      maxDigits = 10,
      placeholder,
      style,
      unit,
      showError = true,
      ...rest
    },
    ref,
  ) => {
    const field = useFieldContext<number | null>();

    const isRequired = isFieldRequired(
      field.options.form.options.validators,
      field.name,
    );

    const hasErrors = Boolean(field.state.meta.errors?.length);

    const id = useId();

    // Track whether user is actively editing (to avoid reformatting while typing)
    const [isEditing, setIsEditing] = useState(false);
    const [editValue, setEditValue] = useState('');

    // Display value: show edit value while editing, formatted value otherwise
    const displayValue = isEditing
      ? editValue
      : formatForInput(field.state.value, maxDigits);

    const handleFocus = () => {
      setIsEditing(true);
      setEditValue(formatForInput(field.state.value, maxDigits));
    };

    const handleBlur = () => {
      setIsEditing(false);
      // Parse and update form state on blur
      const parsed = parseFromInput(editValue);
      field.handleChange(parsed);
      field.handleBlur();
    };

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const inputValue = event.target.value;
      setEditValue(inputValue);

      // Parse and update form state immediately for validation
      const parsed = parseFromInput(inputValue);
      field.handleChange(parsed);
    };

    return (
      <div className={cn('relative', className)}>
        {Boolean(label) && (
          <InputLabel
            className="text-right"
            hint={hint}
            htmlFor={id}
            isRequired={isRequired}
          >
            {label}
          </InputLabel>
        )}
        <div
          className={cn(
            'flex items-center gap-x-2 overflow-hidden transition-colors duration-200 ease-out',
            {
              'flex-auto': flex === 'auto',
              'flex-initial': flex === 'initial',
              'flex-none': flex === 'none',
            },
          )}
          style={style}
        >
          <MuiTextField
            autoComplete={autoComplete}
            className="w-full flex-1 focus:outline-none [&_input]:text-right"
            disabled={isDisabled}
            error={hasErrors}
            id={id}
            inputRef={ref}
            placeholder={placeholder}
            size="small"
            slotProps={{
              htmlInput: { 'aria-label': ariaLabel },
              input: {
                ...(unit
                  ? {
                      endAdornment: (
                        <InputAdornment
                          position="end"
                          className="[&>p]:text-tiga-text-lighter"
                        >
                          {unit}
                        </InputAdornment>
                      ),
                    }
                  : undefined),
                className: 'bg-white',
                inputMode: 'decimal', // Show numeric keyboard on mobile
              },
            }}
            type="text"
            value={displayValue}
            onBlur={handleBlur}
            onChange={handleChange}
            onFocus={handleFocus}
            {...rest}
          />
        </div>
        {showError && hasErrors ? (
          <ValidationError>
            {unique(field.state.meta.errors.map(({ message }) => message)).join(
              ', ',
            )}
          </ValidationError>
        ) : null}
      </div>
    );
  },
);

LocalizedNumberField.displayName = 'LocalizedNumberField';
